import React, { useState } from "react"
import {
    Box,
    Button,
    ButtonVariant,
    componentStyles,
    Text,
    useColors,
    useTheme,
    VerticalStack,
} from "@mallardbay/lib-react-components"

import AssistantPlanStepRow from "~components/outfitter-assistant/assistant-plan-step-row"
import { ASSISTANT_PLAN_STEP_STATUSES } from "~components/outfitter-assistant/assistant-tool-constants"
import type { AssistantPlanPresentation } from "~components/outfitter-assistant/assistant-plan-tool.helpers"
import { TEST_IDS } from "~config/constants"
import COPY from "~config/copy-constants"

interface Props {
    readonly presentation: AssistantPlanPresentation
    readonly isDisabled?: boolean
    readonly onContinueActiveStep?: (message: string) => void
}

export default function AssistantPlanCard({
    presentation,
    isDisabled = false,
    onContinueActiveStep,
}: Props) {
    const styles = useStyles()
    // Track which step was continued (not a bare boolean): the card stays
    // mounted as the plan advances, so each new active step must be actionable.
    const [continuedStepId, setContinuedStepId] = useState<string | null>(null)

    const activeStep = presentation.steps.find(
        (step) => step.status === ASSISTANT_PLAN_STEP_STATUSES.IN_PROGRESS
    )
    const canContinue = Boolean(onContinueActiveStep && activeStep)

    return (
        <Box style={styles.root} testId={TEST_IDS.ASSISTANT_PLAN_CARD}>
            <VerticalStack style={styles.header}>
                <Text style={styles.title}>{presentation.title}</Text>
                {presentation.description ? (
                    <Text style={styles.description}>
                        {presentation.description}
                    </Text>
                ) : null}
            </VerticalStack>
            <VerticalStack style={styles.steps}>
                {presentation.steps.map((step) => (
                    <AssistantPlanStepRow key={step.id} step={step} />
                ))}
            </VerticalStack>
            {canContinue && activeStep ? (
                <VerticalStack style={styles.footer}>
                    <Button
                        variant={ButtonVariant.Solid}
                        isDisabled={
                            isDisabled || continuedStepId === activeStep.id
                        }
                        testId={TEST_IDS.ASSISTANT_PLAN_CONTINUE_BUTTON}
                        onClick={() => {
                            setContinuedStepId(activeStep.id)
                            onContinueActiveStep?.(
                                buildContinueMessage(activeStep.label)
                            )
                        }}
                    >
                        {COPY.AI_CHAT_TOOL_PLAN_CONTINUE}
                    </Button>
                </VerticalStack>
            ) : null}
        </Box>
    )
}

function buildContinueMessage(activeStepLabel: string): string {
    return `${COPY.AI_CHAT_TOOL_PLAN_CONTINUE_MESSAGE_PREFIX} "${activeStepLabel}".`
}

function useStyles() {
    const colors = useColors()
    const theme = useTheme()

    return componentStyles({
        root: {
            width: "100%",
            gap: 3,
            padding: 3,
            borderRadius: theme.radii.lg,
            backgroundColor: colors.bgSecondary,
        },
        header: {
            alignItems: "stretch",
            gap: 1,
        },
        title: {
            fontWeight: theme.fontWeights.semibold,
            color: colors.text,
        },
        description: {
            color: colors.secondaryText,
            fontSize: theme.fontSizes.sm,
        },
        steps: {
            alignItems: "stretch",
            gap: 2,
        },
        footer: {
            alignItems: "stretch",
        },
    })
}
